import { apiFetch } from "./api";

export type Tokens = {
  access_token: string;
  refresh_token: string;
  token_type: string;
  expires_in: number;
};

export type LoginChallenge = {
  type: "ENROLL" | "VERIFY";
  message: string;
  qr_code_data_url?: string;
  secret?: string;
};

export type TwoFactorChallengeResponse = {
  requires_two_factor: true;
  challenge: LoginChallenge;
};

export type ForcedPasswordChangeResponse = {
  requires_password_change: true;
  password_change_token: string;
  token_type: string;
  expires_in: number;
};

export type LoginResponse = Tokens | TwoFactorChallengeResponse | ForcedPasswordChangeResponse;

export type LoginPayload = {
  username: string;
  password: string;
  otp_code?: string;
};

export class TwoFactorRequiredError extends Error {
  challenge: LoginChallenge;

  constructor(challenge: LoginChallenge) {
    super("Two-factor authentication is required.");
    this.challenge = challenge;
  }
}

export class PasswordChangeRequiredError extends Error {
  passwordChangeToken: string;
  expiresIn: number;

  constructor(passwordChangeToken: string, expiresIn: number) {
    super("Password change is required.");
    this.passwordChangeToken = passwordChangeToken;
    this.expiresIn = expiresIn;
  }
}

export function isTwoFactorChallengeResponse(response: LoginResponse): response is TwoFactorChallengeResponse {
  return (response as TwoFactorChallengeResponse).requires_two_factor === true;
}

export function isForcedPasswordChangeResponse(response: LoginResponse): response is ForcedPasswordChangeResponse {
  return (response as ForcedPasswordChangeResponse).requires_password_change === true;
}

export async function loginRequest(payload: LoginPayload): Promise<LoginResponse> {
  return apiFetch<LoginResponse>("/api/auth/login", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function refresh(refresh_token: string): Promise<Tokens> {
  return apiFetch<Tokens>("/api/auth/refresh", {
    method: "POST",
    body: JSON.stringify({ refresh_token }),
  });
}

export type Profile = {
  username: string;
  roles: string[];
  full_name: string | null;
  email?: string | null;
  managed_by_idp?: boolean;
};

export async function me(accessToken: string): Promise<Profile> {
  return apiFetch("/api/auth/me", { method: "GET" }, accessToken);
}

type ProfileUpdatePayload = {
  full_name?: string | null;
};

type ChangePasswordPayload = {
  current_password: string;
  new_password: string;
};

export async function updateProfile(accessToken: string, payload: ProfileUpdatePayload): Promise<Profile> {
  return apiFetch("/api/auth/profile", { method: "PATCH", body: JSON.stringify(payload) }, accessToken);
}

export async function changePassword(accessToken: string, payload: ChangePasswordPayload): Promise<Tokens> {
  return apiFetch<Tokens>("/api/auth/change-password", { method: "POST", body: JSON.stringify(payload) }, accessToken);
}

export async function completeForcedPasswordChange(passwordChangeToken: string, newPassword: string): Promise<{ ok: true }> {
  return apiFetch<{ ok: true }>(
    "/api/auth/complete-forced-password-change",
    { method: "POST", body: JSON.stringify({ new_password: newPassword }) },
    passwordChangeToken
  );
}
